import { useReducer } from 'react';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { loadingReducer, loadingReducerInitialState } from '../../../../reducer/loadingReducer';
import {
  LessonCreateInput,
  LessonTimetableCardsCreateFieldInput,
  useConnectLessonsToTimetableVersionMutation,
  useCreateLessonsMutation,
  useUpdateLessonsMutation,
} from '../../../../types/planung-graphql-client-defs';
import { groupByArray } from '../../../../utils/arrayFunc';
import { hasDurationCountArray, hasLessonUnit } from '../../../../utils/typeguards';
import { LessonFormType, VersionLessonFormType } from '../../types';

type VersionLessonFormWithCardsType =
  | (VersionLessonFormType & { timetableCards?: LessonTimetableCardsCreateFieldInput[] | null })
  | (LessonFormType & { timetableCards?: LessonTimetableCardsCreateFieldInput[] | null });

export function useTimetableLessons({
  organizationUuid,
  schoolYearUuid,
  versionUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
  versionUuid?: string;
}) {
  const [, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const context = useMemorizedCacheTag('LESSONS');

  const [, create] = useCreateLessonsMutation();
  const [, update] = useUpdateLessonsMutation();

  const [, connectLessonsToTimetableVersion] = useConnectLessonsToTimetableVersionMutation();

  const getBasics = (values: LessonFormType) => {
    return {
      subTitle: values.subTitle,
      comment: values.comment,
      teachingLoadEnabled: values.teachingLoadEnabled ?? false,
      timetableEnabled: values.timetableEnabled ?? false,
      elective: values.elective ?? false,
      isClassTrip: values.isClassTrip ?? false,
      onlyInTimetableVersion: true,
    };
  };

  const createLesson = async (values: VersionLessonFormWithCardsType) => {
    const input: LessonCreateInput = {
      organization: { connect: { where: { node: { uuid: organizationUuid } } } },
      ...getBasics(values),
      schoolYear: {
        connect: {
          where: {
            node: {
              uuid: schoolYearUuid,
            },
          },
        },
      },
      subject: {
        Subject: {
          connect: {
            where: {
              node: {
                uuid: values.subject?.uuid,
              },
            },
          },
        },
        SubjectContainer: {
          connect: {
            where: {
              node: {
                uuid: values.subject?.uuid,
              },
            },
          },
        },
      },
      lessonClasses: {
        create: values.lessonClasses?.map((lessonClass, index) => ({
          edge: {
            order: index,
          },
          node: {
            class: {
              connect: {
                where: {
                  node: {
                    uuid: lessonClass.class.uuid,
                  },
                },
              },
            },
            ...(lessonClass.groups &&
              lessonClass.groups.length > 0 && {
                groups: {
                  connect: lessonClass.groups.map((group) => ({ where: { node: { uuid: group.uuid } } })),
                },
              }),
          },
        })),
      },
      teachers: {
        connect: values.teachers?.map((teacher, index) => {
          return {
            where: {
              node: { uuid: teacher.person?.uuid },
            },
            edge: {
              order: index,
              description: teacher.description,
              present: teacher.present,
              teachingLoad: teacher.teachingLoad,
              teachingLoadHours: teacher.teachingLoadHours,
              writesCertificate: teacher.writesCertificate,
            },
          };
        }),
      },
      roomSupply: {
        connect: values.roomSupply?.map((room, index) => {
          return {
            where: {
              node: { uuid: room.uuid },
            },
            edge: {
              order: index,
              descriptions: room.description,
            },
          };
        }),
      },
      versions: {
        connect: [
          {
            where: {
              node: { uuid: versionUuid },
            },
          },
        ],
      },
    };

    if (hasDurationCountArray(values)) {
      const groups = groupByArray(values.durationCountArray, (v) => v.duration);
      const timetableCardsCreate: LessonTimetableCardsCreateFieldInput[] = [];

      groups.forEach((group) => {
        group.forEach((durationCount) => {
          const count = durationCount.count ? Array.from(Array(Math.abs(durationCount.count)), (x, i) => i) : [];
          count.forEach(() => {
            timetableCardsCreate.push({
              node: {
                timetableVersion: { connect: { where: { node: { uuid: versionUuid } } } },
                duration: durationCount.duration ?? 1,
                locked: false,
              },
            });
          });
        });
      });

      input.timetableCards = {
        create: timetableCardsCreate,
      };
    } else if (hasLessonUnit(values)) {
      const timetableCardsCreate: LessonTimetableCardsCreateFieldInput[] = [];
      values.lessonUnit.forEach((lessonUnit) => {
        const count = lessonUnit.count ? Array.from(Array(Math.abs(lessonUnit.count)), (x, i) => i) : [];
        count.forEach(() => {
          timetableCardsCreate.push({
            node: {
              timetableVersion: { connect: { where: { node: { uuid: versionUuid } } } },
              duration: lessonUnit.duration ?? 1,
              locked: false,
            },
          });
        });
      });
      input.timetableCards = {
        create: timetableCardsCreate,
      };
    }

    if (values.timetableCards) {
      input.timetableCards = {
        create: values.timetableCards,
      };
    }

    return await create({ input: input });
  };

  const updateLesson = async (values: LessonFormType) => {
    dispatch({ type: 'SET_LOADING', uuids: [values.uuid ?? 'all'] });

    const result = await update(
      {
        where: { uuid: values.uuid },
        update: {
          ...getBasics(values),
          schoolYear: {
            disconnect: {},
            connect: {
              where: {
                node: {
                  uuid: schoolYearUuid,
                },
              },
            },
          },
          subject: {
            Subject: {
              disconnect: {},
              connect: {
                where: {
                  node: {
                    uuid: values.subject?.uuid,
                  },
                },
              },
            },
          },
          lessonClasses: [
            {
              delete: [{}],
              create: values.lessonClasses?.map((lessonClass, index) => ({
                edge: {
                  order: index,
                },
                node: {
                  class: {
                    connect: {
                      where: {
                        node: {
                          uuid: lessonClass.class.uuid,
                        },
                      },
                    },
                  },
                  ...(lessonClass.groups &&
                    lessonClass.groups.length > 0 && {
                      groups: {
                        connect: lessonClass.groups.map((group) => ({ where: { node: { uuid: group.uuid } } })),
                      },
                    }),
                },
              })),
            },
          ],
          lessonUnit: [
            {
              delete: [{}],
              create: values.lessonUnit?.map((lessonUnit, index) => {
                return {
                  edge: { order: index },
                  node: {
                    count: lessonUnit.count,
                    duration: lessonUnit.duration,
                    ...(lessonUnit.subjectContainerUuid && {
                      subjectContainer: {
                        connect: {
                          where: {
                            node: {
                              uuid: lessonUnit.subjectContainerUuid,
                            },
                          },
                        },
                      },
                    }),
                  },
                };
              }),
            },
          ],
          teachers: [
            {
              disconnect: [{}],
              connect: values.teachers?.map((teacher, index) => {
                return {
                  where: {
                    node: { uuid: teacher.person?.uuid },
                  },
                  edge: {
                    order: index,
                    description: teacher.description,
                    present: teacher.present,
                    teachingLoad: teacher.teachingLoad,
                    teachingLoadHours: teacher.teachingLoadHours,
                    writesCertificate: teacher.writesCertificate,
                  },
                };
              }),
            },
          ],
          roomSupply: [
            {
              disconnect: [{}],
              connect: values.roomSupply?.map((room, index) => {
                return {
                  where: {
                    node: { uuid: room.uuid },
                  },
                  edge: {
                    order: index,
                    descriptions: room.description,
                  },
                };
              }),
            },
          ],
        },
      },
      context,
    );
    dispatch({ type: 'RESET' });
    return result;
  };

  const addLessonsToVersion = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    let result: { error?: unknown; success?: boolean; data: string[] } = { data: [] };

    if (versionUuid && uuids) {
      const res = await connectLessonsToTimetableVersion(
        {
          organizationUuid: organizationUuid,
          lessonUuids: uuids,
          timetableVersionUuid: versionUuid,
        },
        { additionalTypenames: ['Lesson', 'TimetableCard', 'ClassGridCard'] },
      );
      result = { error: res.error, success: true, data: res.data?.connectLessonsToTimetableVersion?.nodeUuids ?? [] };
    } else {
      result = { error: { message: 'version not found', name: 'version not found' }, success: false, data: [] };
    }
    dispatch({ type: 'RESET' });
    return result;
  };

  return {
    createLesson,
    updateLesson,
    addLessonsToVersion,
    lessonContext: context,
  };
}
